import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { MyRecipesCard } from "@/components/dashboard/MyRecipesCard";
import { FavoriteRecipesCard } from "@/components/dashboard/FavoriteRecipesCard";
import { SubscriptionsCard } from "@/components/dashboard/SubscriptionsCard";
import { FeedCard } from "@/components/dashboard/FeedCard";
import { PackagesCard } from "@/components/dashboard/PackagesCard";
import { CreateButton } from "@/components/dashboard/CreateButton";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UserCog } from "lucide-react";
import { CreatorTermsDialog } from "@/components/ui/alert-dialog-terms";

const Dashboard = () => {
  const navigate = useNavigate();
  const { favoriteRecipes, subscriptions, myRecipes, loading } = useDashboardData();
  const [isCreator, setIsCreator] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    checkAuth();
    checkUserStatus();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error("Authentication error. Please log in again.");
      navigate("/login");
    } finally {
      setCheckingAuth(false);
    }
  };

  const checkUserStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('creator_status, application_status, is_superuser')
        .eq('id', user.id)
        .single();

      if (error) {
        throw error;
      }

      if (profile) {
        setIsCreator(profile.creator_status === 'creator');
        setApplicationStatus(profile.application_status);
        setIsSuperAdmin(profile.is_superuser);
      }
    } catch (error) {
      console.error("Error checking user status:", error);
      toast.error("Failed to load profile status");
    }
  };

  const handleCreatorApplication = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .update({
          application_status: 'pending',
          application_date: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setApplicationStatus('pending');
      toast.success("Creator application submitted successfully!");
      setShowTerms(false);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application");
    }
  };

  if (checkingAuth || loading) {
    return <div>Loading...</div>;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 container max-w-7xl mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold">Dashboard</h1>
              {isCreator ? (
                <CreateButton />
              ) : (
                <Button onClick={() => setShowTerms(true)} className="bg-primary hover:bg-primary/90">
                  <UserCog className="w-4 h-4 mr-2" />
                  Apply as Creator
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isSuperAdmin && (
                <>
                  <FeedCard />
                  {isCreator && <PackagesCard />}
                </>
              )}
              {isCreator && <MyRecipesCard recipes={myRecipes} />}
              <FavoriteRecipesCard recipes={favoriteRecipes} />
              <SubscriptionsCard profiles={subscriptions} />
            </div>
          </main>
          <Footer />
        </div>
      </div>

      <CreatorTermsDialog
        open={showTerms}
        onOpenChange={setShowTerms}
        onConfirm={handleCreatorApplication}
      />
    </SidebarProvider>
  );
};

export default Dashboard;
